import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Search, Download, IndianRupee, Users, CheckCircle, Send, Calendar, 
  XCircle, AlertCircle, ChevronRight, Eye, MessageSquare, Check, X,
  Plus, UserPlus, Building, MapPin, Clock, Loader2, User, Phone, Trash2
} from 'lucide-react';

// Custom hook for debounced value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function TolobaPage() {
  const { api, isAdmin, isSuperAdmin, user, isTolobaCollector, tolobaAccess } = useAuth();
  
  // Main view state
  const [activeView, setActiveView] = useState('campaigns'); // 'campaigns' or 'collection'
  
  // Campaign list state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('active');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  
  // Create campaign dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '16mi Raat Darees',
    month: new Date().toISOString().slice(0, 7),
    description: '',
    sectors: []
  });
  const [availableSectors, setAvailableSectors] = useState([]);
  const [selectedSectorsForCreate, setSelectedSectorsForCreate] = useState([]);
  
  // Assignment dialogs
  const [showAssignIncharge, setShowAssignIncharge] = useState(false);
  const [showAssignMohallaHead, setShowAssignMohallaHead] = useState(false);
  const [showAssignKG, setShowAssignKG] = useState(false);
  const [assignMohalla, setAssignMohalla] = useState('');
  const [assignSector, setAssignSector] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchingMembers, setSearchingMembers] = useState(false);
  
  // Campaign collection state (for KG/Mohalla Head to collect within campaign dialog)
  const [campaignFamilies, setCampaignFamilies] = useState([]);
  const [campaignFamiliesLoading, setCampaignFamiliesLoading] = useState(false);
  const [campaignCollectionSearch, setCampaignCollectionSearch] = useState('');
  
  // Collection view state (original toloba functionality)
  const [contributions, setContributions] = useState([]);
  const [summary, setSummary] = useState({ mohalla_totals: {}, sector_totals: {} });
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [total, setTotal] = useState(0);
  const [familyCount, setFamilyCount] = useState(0);
  const [contributedCount, setContributedCount] = useState(0);
  const [visitedCount, setVisitedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Payment dialog state
  const [paymentItem, setPaymentItem] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [sendThankYou, setSendThankYou] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Not Paid dialog
  const [notPaidItem, setNotPaidItem] = useState(null);
  const [notPaidReason, setNotPaidReason] = useState('');
  
  // Summary dialog
  const [showSummary, setShowSummary] = useState(false);
  const [mohallaSummary, setMohallaSummary] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  // KG Sector View State
  const [selectedKGSector, setSelectedKGSector] = useState(null);
  const [kgSectorFamilies, setKgSectorFamilies] = useState([]);
  const [kgSectorLoading, setKgSectorLoading] = useState(false);
  const [showKGSectorDialog, setShowKGSectorDialog] = useState(false);

  // Use validated toloba access from context (already validated against active campaigns)
  const userTolobaAccess = tolobaAccess || [];
  const isKGFromAccess = userTolobaAccess.some(access => access.role === 'khidmat_guzar');
  const userKGSectors = userTolobaAccess
    .filter(access => access.role === 'khidmat_guzar')
    .flatMap(access => access.sectors || []);
  const kgCampaignMonth = userTolobaAccess.find(access => access.role === 'khidmat_guzar')?.month;

  const isKhidmatGuzar = user?.leadership_role === 'musaid' || user?.leadership_role === 'musaida';
  const isMasool = user?.leadership_role === 'masool' || user?.leadership_role === 'masoola';
  const canEdit = isAdmin || isSuperAdmin || isTolobaCollector || isKhidmatGuzar || isMasool;
  
  // Check if user is a Campaign Incharge for the selected campaign
  const isAssignedCampaignIncharge = campaignDetails?.campaign_incharges?.some(
    incharge => incharge.its_id === user?.its_id || incharge.member_id === user?.member_id
  );
  
  // canManageCampaigns now includes Campaign Incharges
  const canManageCampaigns = isAdmin || isSuperAdmin || isAssignedCampaignIncharge;
  
  // Check if user is a Mohalla Head for the selected campaign
  const isAssignedMohallaHead = campaignDetails?.mohalla_heads?.some(
    head => head.its_id === user?.its_id || head.member_id === user?.member_id
  );
  const canAssignKG = canManageCampaigns || isAssignedMohallaHead;
  const userMohallaInCampaign = campaignDetails?.mohalla_heads?.find(
    head => head.its_id === user?.its_id || head.member_id === user?.member_id
  )?.mohalla;

  // Check if user is an assigned KG for the selected campaign
  const isAssignedKG = campaignDetails?.khidmat_guzars?.some(
    kg => kg.its_id === user?.its_id || kg.member_id === user?.member_id
  );
  // Get ALL sectors assigned to this KG (they could be assigned to multiple sectors)
  const userSectorsInCampaign = campaignDetails?.khidmat_guzars
    ?.filter(kg => kg.its_id === user?.its_id || kg.member_id === user?.member_id)
    ?.map(kg => kg.sector) || [];

  // Determine which sectors to show for collection based on role
  const getCollectionSectors = () => {
    if (isAdmin || isSuperAdmin || isAssignedCampaignIncharge) {
      // Admin/Incharge sees all campaign sectors
      return campaignDetails?.sectors || [];
    } else if (isAssignedMohallaHead && userMohallaInCampaign) {
      // Mohalla Head sees only sectors in their mohalla
      return allowedSectorsForKG || [];
    } else if (isAssignedKG && userSectorsInCampaign.length > 0) {
      // KG sees only their assigned sectors
      return userSectorsInCampaign;
    }
    return [];
  };

  // State for filtered sectors (for Mohalla Head)
  const [allowedSectorsForKG, setAllowedSectorsForKG] = useState([]);

  // Fetch sectors belonging to a mohalla
  const fetchSectorsByMohalla = useCallback(async (mohallaName) => {
    try {
      const response = await api.get(`/sectors/by-mohalla/${encodeURIComponent(mohallaName)}`);
      return response.data.sectors || [];
    } catch (error) {
      console.error('Failed to fetch sectors by mohalla:', error);
      return [];
    }
  }, [api]);

  // When campaign details load and user is Mohalla Head, filter sectors
  useEffect(() => {
    const loadAllowedSectors = async () => {
      if (isAssignedMohallaHead && userMohallaInCampaign && campaignDetails?.sectors) {
        const mohallaSectors = await fetchSectorsByMohalla(userMohallaInCampaign);
        // Only show sectors that are BOTH in the campaign AND in the user's mohalla
        const filtered = campaignDetails.sectors.filter(s => mohallaSectors.includes(s));
        setAllowedSectorsForKG(filtered);
      } else if (canManageCampaigns && campaignDetails?.sectors) {
        // Admin can assign to all campaign sectors
        setAllowedSectorsForKG(campaignDetails.sectors);
      }
    };
    loadAllowedSectors();
  }, [isAssignedMohallaHead, userMohallaInCampaign, campaignDetails, canManageCampaigns, fetchSectorsByMohalla]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      setCampaignsLoading(true);
      const response = await api.get(`/toloba-campaigns?status=${campaignStatusFilter}`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  }, [api, campaignStatusFilter]);

  // Fetch campaign details
  const fetchCampaignDetails = useCallback(async (campaignId) => {
    try {
      const response = await api.get(`/toloba-campaigns/${campaignId}`);
      setCampaignDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch campaign details');
    }
  }, [api]);

  // Fetch families for collection within a campaign
  const fetchCampaignFamilies = useCallback(async (campaignMonth, campaignSectors) => {
    if (!campaignMonth || !campaignSectors?.length) return;
    try {
      setCampaignFamiliesLoading(true);
      const params = new URLSearchParams();
      params.append('month', campaignMonth);
      // Fetch for all campaign sectors
      const response = await api.get(`/toloba?${params}`);
      // Filter to only include families in the campaign's sectors
      const allContributions = response.data.contributions || [];
      const filtered = allContributions.filter(c => campaignSectors.includes(c.sector));
      setCampaignFamilies(filtered);
    } catch (error) {
      console.error('Failed to fetch campaign families');
    } finally {
      setCampaignFamiliesLoading(false);
    }
  }, [api]);

  // Fetch families for a specific KG sector
  const fetchKGSectorFamilies = useCallback(async (sector) => {
    if (!sector || !kgCampaignMonth) return;
    try {
      setKgSectorLoading(true);
      const params = new URLSearchParams();
      params.append('month', kgCampaignMonth);
      const response = await api.get(`/toloba?${params}`);
      const allContributions = response.data.contributions || [];
      // Filter to only this sector
      const filtered = allContributions.filter(c => c.sector === sector);
      setKgSectorFamilies(filtered);
    } catch (error) {
      console.error('Failed to fetch sector families');
      toast.error('Failed to load families');
    } finally {
      setKgSectorLoading(false);
    }
  }, [api, kgCampaignMonth]);

  // Auto-load families for KG when they open the campaign dialog
  useEffect(() => {
    if (showCampaignDialog && campaignDetails && isAssignedKG && userSectorsInCampaign.length > 0 && campaignFamilies.length === 0) {
      // Auto-load families for KG's assigned sectors
      fetchCampaignFamilies(campaignDetails.month, userSectorsInCampaign);
    }
  }, [showCampaignDialog, campaignDetails, isAssignedKG, userSectorsInCampaign, fetchCampaignFamilies, campaignFamilies.length]);

  // Fetch sectors for campaign creation
  const fetchAvailableSectors = useCallback(async () => {
    try {
      const response = await api.get('/sectors');
      const sectorData = response.data.sectors || [];
      setAvailableSectors(sectorData.map(s => typeof s === 'string' ? s : s.name));
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  }, [api]);

  // Create campaign
  const createCampaign = async () => {
    if (!newCampaign.name || !newCampaign.month || selectedSectorsForCreate.length === 0) {
      toast.error('Please fill all required fields and select at least one sector');
      return;
    }
    try {
      setSaving(true);
      await api.post('/toloba-campaigns', {
        ...newCampaign,
        sectors: selectedSectorsForCreate
      });
      toast.success('Campaign created successfully');
      setShowCreateDialog(false);
      setNewCampaign({
        name: '16mi Raat Darees',
        month: new Date().toISOString().slice(0, 7),
        description: '',
        sectors: []
      });
      setSelectedSectorsForCreate([]);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  // Assign handlers
  const handleAssignIncharge = async () => {
    if (!selectedMember || !selectedCampaign) return;
    try {
      setSaving(true);
      await api.post(`/toloba-campaigns/${selectedCampaign.id}/incharges`, {
        member_id: selectedMember.id
      });
      toast.success(`${selectedMember.full_name} assigned as Campaign Incharge`);
      setShowAssignIncharge(false);
      setSelectedMember(null);
      setMemberSearch('');
      fetchCampaignDetails(selectedCampaign.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign incharge');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignMohallaHead = async () => {
    if (!selectedMember || !selectedCampaign || !assignMohalla) return;
    try {
      setSaving(true);
      await api.post(`/toloba-campaigns/${selectedCampaign.id}/mohalla-heads`, {
        member_id: selectedMember.id,
        mohalla: assignMohalla
      });
      toast.success(`${selectedMember.full_name} assigned as Mohalla Head for ${assignMohalla}`);
      setShowAssignMohallaHead(false);
      setSelectedMember(null);
      setMemberSearch('');
      setAssignMohalla('');
      fetchCampaignDetails(selectedCampaign.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign Mohalla Head');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignKG = async () => {
    if (!selectedMember || !selectedCampaign || !assignSector) return;
    try {
      setSaving(true);
      await api.post(`/toloba-campaigns/${selectedCampaign.id}/khidmat-guzars`, {
        member_id: selectedMember.id,
        sector: assignSector
      });
      toast.success(`${selectedMember.full_name} assigned as Khidmat Guzar for ${assignSector}`);
      setShowAssignKG(false);
      setSelectedMember(null);
      setMemberSearch('');
      setAssignSector('');
      fetchCampaignDetails(selectedCampaign.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign Khidmat Guzar');
    } finally {
      setSaving(false);
    }
  };

  // Remove handlers for admin
  const handleRemoveIncharge = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} as Incharge?`)) return;
    try {
      await api.delete(`/toloba-campaigns/${selectedCampaign.id}/incharges/${memberId}`);
      toast.success(`${memberName} removed as Incharge`);
      fetchCampaignDetails(selectedCampaign.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove Incharge');
    }
  };

  const handleRemoveMohallaHead = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} as Mohalla Head?`)) return;
    try {
      await api.delete(`/toloba-campaigns/${selectedCampaign.id}/mohalla-heads/${memberId}`);
      toast.success(`${memberName} removed as Mohalla Head`);
      fetchCampaignDetails(selectedCampaign.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove Mohalla Head');
    }
  };

  const handleRemoveKG = async (sector, memberName) => {
    if (!window.confirm(`Remove ${memberName} from ${sector}?`)) return;
    try {
      await api.delete(`/toloba-campaigns/${selectedCampaign.id}/khidmat-guzars/${encodeURIComponent(sector)}`);
      toast.success(`Khidmat Guzar removed from ${sector}`);
      fetchCampaignDetails(selectedCampaign.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove Khidmat Guzar');
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    if (!window.confirm(`Are you sure you want to delete the campaign "${selectedCampaign.name}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/toloba-campaigns/${selectedCampaign.id}`);
      toast.success(`Campaign "${selectedCampaign.name}" deleted successfully`);
      setShowCampaignDialog(false);
      setSelectedCampaign(null);
      setCampaignDetails(null);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  // Original collection functions
  const fetchContributions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      if (selectedMohalla) params.append('mohalla', selectedMohalla);
      if (selectedSector) params.append('sector', selectedSector);
      
      const response = await api.get(`/toloba?${params}`);
      setContributions(response.data.contributions || []);
      setTotal(response.data.total || 0);
      setFamilyCount(response.data.family_count || 0);
      setContributedCount(response.data.paid_count || response.data.contributed_count || 0);
      setVisitedCount(response.data.visited_count || 0);
      setPendingCount(response.data.pending_count || 0);
      setSummary(response.data.summary || { mohalla_totals: {}, sector_totals: {} });
    } catch (error) {
      toast.error('Failed to fetch contributions');
    } finally {
      setLoading(false);
    }
  }, [api, selectedMonth, selectedMohalla, selectedSector]);

  const fetchMohallas = useCallback(async () => {
    try {
      const response = await api.get('/mohallas');
      const mohallaData = response.data.mohallas || [];
      setMohallas(mohallaData.map(m => typeof m === 'string' ? m : m.name));
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  }, [api]);

  const fetchSectors = useCallback(async () => {
    try {
      const response = await api.get('/sectors');
      const sectorData = response.data.sectors || [];
      setSectors(sectorData.map(s => typeof s === 'string' ? s : s.name));
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  }, [api]);

  useEffect(() => {
    fetchCampaigns();
    // Only fetch mohallas and sectors when needed (lazy load)
  }, [fetchCampaigns]);

  // Lazy load mohallas and sectors only when Collection tab is active
  useEffect(() => {
    if (activeView === 'collection') {
      fetchMohallas();
      fetchSectors();
      fetchContributions();
    }
  }, [activeView, fetchMohallas, fetchSectors, fetchContributions]);

  // Lazy load available sectors only when create dialog is opened
  useEffect(() => {
    if (showCreateDialog && availableSectors.length === 0) {
      fetchAvailableSectors();
    }
  }, [showCreateDialog, availableSectors.length, fetchAvailableSectors]);

  // Fetch mohallas when Assign Mohalla Head dialog is opened
  useEffect(() => {
    if (showAssignMohallaHead && mohallas.length === 0) {
      fetchMohallas();
    }
  }, [showAssignMohallaHead, mohallas.length, fetchMohallas]);

  // Debounced search for member assignment
  useEffect(() => {
    if (!memberSearch || memberSearch.length === 0) {
      setMemberResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        setSearchingMembers(true);
        const response = await api.get(`/members?search=${encodeURIComponent(memberSearch)}&limit=20`);
        setMemberResults(response.data.members || []);
      } catch (error) {
        console.error('Failed to search members');
        setMemberResults([]);
      } finally {
        setSearchingMembers(false);
      }
    }, 400);
    
    return () => clearTimeout(timer);
  }, [memberSearch, api]);

  const handlePaid = async () => {
    if (!paymentItem || !paymentAmount) return;
    try {
      setSaving(true);
      await api.post(`/toloba/${paymentItem.hof_id}`, {
        hof_id: paymentItem.hof_id,
        amount: parseFloat(paymentAmount),
        month: selectedMonth,
        remarks: paymentRemarks
      });
      
      if (sendThankYou) {
        try {
          await api.post(`/toloba/${paymentItem.hof_id}/receipt?month=${selectedMonth}`);
        } catch {}
      }
      
      toast.success('Payment recorded successfully');
      setPaymentItem(null);
      setPaymentAmount('');
      setPaymentRemarks('');
      fetchContributions();
      // Also refresh campaign families if in campaign dialog
      if (campaignDetails) {
        fetchCampaignFamilies(campaignDetails.month, getCollectionSectors());
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  // Quick mark as visited (not paid) for KG view
  const handleQuickVisited = async (family) => {
    try {
      setSaving(true);
      await api.post(`/toloba/${family.hof_id}`, {
        hof_id: family.hof_id,
        amount: 0,
        month: kgCampaignMonth,
        remarks: 'Visited - Not Paid'
      });
      toast.success('Marked as Visited');
      // Refresh the sector families
      if (selectedKGSector) {
        await fetchKGSectorFamilies(selectedKGSector);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // Handle payment for KG view
  const handleKGPayment = async () => {
    if (!paymentItem || !paymentAmount) return;
    try {
      setSaving(true);
      await api.post(`/toloba/${paymentItem.hof_id}`, {
        hof_id: paymentItem.hof_id,
        amount: parseFloat(paymentAmount),
        month: kgCampaignMonth,
        remarks: paymentRemarks || ''
      });
      toast.success('Payment recorded successfully');
      setPaymentItem(null);
      setPaymentAmount('');
      setPaymentRemarks('');
      // Refresh the sector families
      if (selectedKGSector) {
        await fetchKGSectorFamilies(selectedKGSector);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleNotPaid = async () => {
    if (!notPaidItem) return;
    try {
      setSaving(true);
      await api.post(`/toloba/${notPaidItem.hof_id}`, {
        hof_id: notPaidItem.hof_id,
        amount: 0,
        month: selectedMonth,
        remarks: notPaidReason || 'Visited - Not Paid'
      });
      toast.success('Marked as Visited');
      setNotPaidItem(null);
      setNotPaidReason('');
      fetchContributions();
      // Also refresh campaign families if in campaign dialog
      if (campaignDetails) {
        fetchCampaignFamilies(campaignDetails.month, getCollectionSectors());
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const filteredContributions = contributions.filter(c => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      c.hof_name?.toLowerCase().includes(searchLower) ||
      c.hof_its?.includes(search) ||
      c.mobile?.includes(search)
    );
  });

  const formatMonth = useCallback((month) => {
    if (!month) return '';
    const [year, m] = month.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(m) - 1]} ${year}`;
  }, []);

  // Memoized Campaign Card Component for performance
  const CampaignCard = memo(({ campaign, onSelect }) => (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500"
      onClick={() => onSelect(campaign)}
      data-testid={`campaign-card-${campaign.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{campaign.name}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <Calendar className="w-4 h-4" />
              {formatMonth(campaign.month)}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
              {campaign.status}
            </Badge>
            {campaign.user_role_in_campaign && (
              <Badge className={
                campaign.user_role_in_campaign === 'khidmat_guzar' ? 'bg-purple-100 text-purple-700' :
                campaign.user_role_in_campaign === 'mohalla_head' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }>
                {campaign.user_role_in_campaign === 'khidmat_guzar' ? 'Khidmat Guzar' :
                 campaign.user_role_in_campaign === 'mohalla_head' ? 'Mohalla Head' :
                 'Incharge'}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Assignment Status */}
        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <div className={`text-center p-1.5 rounded ${campaign.incharge_count > 0 ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-400'}`}>
            <p className="font-medium">{campaign.incharge_count || 0} Incharge</p>
          </div>
          <div className={`text-center p-1.5 rounded ${campaign.mohalla_head_count > 0 ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-400'}`}>
            <p className="font-medium">{campaign.mohalla_head_count || 0} MH</p>
          </div>
          <div className={`text-center p-1.5 rounded ${campaign.kg_count > 0 ? 'bg-purple-50 text-purple-700' : 'bg-slate-50 text-slate-400'}`}>
            <p className="font-medium">{campaign.kg_count || 0} KG</p>
          </div>
        </div>
        
        {/* Collection Stats */}
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="text-center p-2 bg-slate-50 rounded">
            <p className="text-slate-500">Families</p>
            <p className="font-bold text-lg">{campaign.family_count || 0}</p>
          </div>
          <div className="text-center p-2 bg-emerald-50 rounded">
            <p className="text-slate-500">Collected</p>
            <p className="font-bold text-lg text-emerald-600">{campaign.collected_count || 0}</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded">
            <p className="text-slate-500">Amount</p>
            <p className="font-bold text-lg text-blue-600">₹{(campaign.total_collected || 0).toLocaleString('en-IN')}</p>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Progress</span>
            <span>{campaign.progress_percent || 0}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all"
              style={{ width: `${campaign.progress_percent || 0}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  ));

  // Memoized callback for campaign selection
  const handleCampaignSelect = useCallback((campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignDetails(campaign.id);
    setShowCampaignDialog(true);
  }, [fetchCampaignDetails]);

  // Member Search Component for Assignments - Using stable component pattern
  const renderMemberSearch = () => (
    <div className="space-y-3">
      <div>
        <Label>Search Member by Name / ITS / Mobile</Label>
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Start typing to search..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>
      </div>
      
      {searchingMembers && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      )}
      
      {!searchingMembers && memberResults.length > 0 && (
        <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
          {memberResults.map(member => (
            <div
              key={member.id}
              className={`p-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                selectedMember?.id === member.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''
              }`}
              onClick={() => setSelectedMember(member)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{member.full_name}</p>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">
                      {member.its_id || 'N/A'}
                    </span>
                    <span className="truncate">{member.mohalla}</span>
                    <span>•</span>
                    <span>{member.sector}</span>
                  </div>
                </div>
                {member.mobile && (
                  <div className="flex items-center gap-1 text-sm text-slate-500 shrink-0">
                    <Phone className="w-3.5 h-3.5" />
                    <span>{member.mobile}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {!searchingMembers && memberSearch && memberResults.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-4">No members found for "{memberSearch}"</p>
      )}
      
      {selectedMember && (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-emerald-800">{selectedMember.full_name}</p>
              <p className="text-sm text-emerald-600">
                ITS: {selectedMember.its_id || 'N/A'} • {selectedMember.sector}
              </p>
            </div>
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
        </div>
      )}
    </div>
  );

  // KG Sector Card Component
  const KGSectorCard = ({ sector, index }) => {
    // Calculate stats for this sector from existing data (if loaded)
    const sectorFamilies = campaignFamilies.filter(f => f.sector === sector);
    const totalFamilies = sectorFamilies.length;
    const paidFamilies = sectorFamilies.filter(f => f.amount > 0).length;
    const visitedNotPaid = sectorFamilies.filter(f => f.status === 'visited' || (f.remarks && f.amount === 0)).length;
    
    return (
      <Card 
        className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-purple-500 hover:border-l-purple-600"
        onClick={() => {
          setSelectedKGSector(sector);
          fetchKGSectorFamilies(sector);
          setShowKGSectorDialog(true);
        }}
        data-testid={`kg-sector-card-${index}`}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">{sector}</CardTitle>
              <CardDescription>Toloba Collection</CardDescription>
            </div>
            <Badge className="bg-purple-100 text-purple-700">
              <IndianRupee className="w-3 h-3 mr-1" />
              Collection
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Click to start collection</span>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  };

  // If user is a KG (from toloba_access), show sector cards directly
  if (isKGFromAccess && !isAdmin && !isSuperAdmin) {
    return (
      <div className="space-y-6" data-testid="toloba-page">
        {/* Header for KG */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Toloba - Collection</h1>
            <p className="text-slate-500">Your assigned sectors for collection</p>
          </div>
        </div>

        {/* KG Sector Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {userKGSectors.map((sector, index) => (
            <KGSectorCard key={sector} sector={sector} index={index} />
          ))}
        </div>

        {userKGSectors.length === 0 && (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No Sectors Assigned</h3>
            <p className="text-slate-500 mt-2">You have not been assigned any sectors for collection</p>
          </Card>
        )}

        {/* KG Sector Dialog */}
        <Dialog open={showKGSectorDialog} onOpenChange={setShowKGSectorDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedKGSector} - Collection</DialogTitle>
              <DialogDescription>
                Mark families as Paid or Visited (Not Paid)
              </DialogDescription>
            </DialogHeader>
            
            {kgSectorLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="p-4 bg-slate-50">
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-2xl font-bold">{kgSectorFamilies.length}</p>
                  </Card>
                  <Card className="p-4 bg-emerald-50">
                    <p className="text-sm text-emerald-600">Paid</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {kgSectorFamilies.filter(f => f.status === 'paid' || f.amount > 0).length}
                    </p>
                  </Card>
                  <Card className="p-4 bg-amber-50">
                    <p className="text-sm text-amber-600">Visited</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {kgSectorFamilies.filter(f => f.status === 'visited' || (f.remarks && f.amount === 0)).length}
                    </p>
                  </Card>
                  <Card className={`p-4 ${
                    kgSectorFamilies.length > 0 && 
                    kgSectorFamilies.filter(f => f.status === 'paid' || f.status === 'visited' || f.amount > 0 || f.remarks).length === kgSectorFamilies.length
                      ? 'bg-green-100 border-green-500' 
                      : 'bg-blue-50'
                  }`}>
                    <p className="text-sm text-blue-600">Progress</p>
                    <p className={`text-2xl font-bold ${
                      kgSectorFamilies.length > 0 && 
                      kgSectorFamilies.filter(f => f.status === 'paid' || f.status === 'visited' || f.amount > 0 || f.remarks).length === kgSectorFamilies.length
                        ? 'text-green-600' 
                        : 'text-blue-600'
                    }`}>
                      {kgSectorFamilies.length > 0 
                        ? Math.round((kgSectorFamilies.filter(f => f.status === 'paid' || f.status === 'visited' || f.amount > 0 || f.remarks).length / kgSectorFamilies.length) * 100)
                        : 0}%
                    </p>
                  </Card>
                </div>

                {/* 100% Completed Banner */}
                {kgSectorFamilies.length > 0 && 
                 kgSectorFamilies.filter(f => f.status === 'paid' || f.status === 'visited' || f.amount > 0 || f.remarks).length === kgSectorFamilies.length && (
                  <div className="p-4 bg-green-100 border border-green-500 rounded-lg text-center">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <p className="text-lg font-bold text-green-700">100% Completed!</p>
                    <p className="text-sm text-green-600">All families have been visited or paid</p>
                  </div>
                )}

                {/* Families Table */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>HOF Name</TableHead>
                      <TableHead>ITS</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kgSectorFamilies.map((family, idx) => (
                      <TableRow key={family.hof_id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell className="font-medium">{family.hof_name}</TableCell>
                        <TableCell>{family.hof_its}</TableCell>
                        <TableCell>
                          {(family.status === 'paid' || family.amount > 0) ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              ₹{family.amount}
                            </Badge>
                          ) : (family.status === 'visited' || family.remarks) ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Check className="w-3 h-3 mr-1" />
                              Visited
                            </Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {family.status !== 'paid' && family.status !== 'visited' && family.amount === 0 && !family.remarks && (
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-amber-600 hover:bg-amber-50"
                                disabled={saving}
                                onClick={() => {
                                  // Mark as visited (not paid)
                                  handleQuickVisited(family);
                                }}
                              >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                                Visited
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700"
                                disabled={saving}
                                onClick={() => setPaymentItem(family)}
                              >
                                <IndianRupee className="w-4 h-4 mr-1" />
                                Paid
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={!!paymentItem} onOpenChange={() => setPaymentItem(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
              <DialogDescription>
                {paymentItem?.hof_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Remarks (Optional)</Label>
                <Textarea
                  placeholder="Any notes..."
                  value={paymentRemarks}
                  onChange={(e) => setPaymentRemarks(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentItem(null)}>
                Cancel
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleKGPayment}
                disabled={!paymentAmount || saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                OK
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="toloba-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Toloba - 16mi Raat Darees</h1>
          <p className="text-slate-500">Manage monthly voluntary contributions</p>
        </div>
        
        {(isAdmin || isSuperAdmin) && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        )}
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="campaigns" data-testid="campaigns-tab">
            <Calendar className="w-4 h-4 mr-2" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="collection" data-testid="collection-tab">
            <IndianRupee className="w-4 h-4 mr-2" />
            Collection
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-6">
          {/* Status Filter Tabs */}
          <div className="flex items-center justify-between mb-4">
            <Tabs value={campaignStatusFilter} onValueChange={setCampaignStatusFilter}>
              <TabsList className="bg-slate-100">
                <TabsTrigger value="active" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
                  Active
                </TabsTrigger>
                <TabsTrigger value="completed" className="data-[state=active]:bg-slate-600 data-[state=active]:text-white">
                  Completed
                </TabsTrigger>
                <TabsTrigger value="all" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  All
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-sm text-slate-500">{campaigns.length} campaign(s)</p>
          </div>
          
          {campaignsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : campaigns.length === 0 ? (
            <Card className="p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Campaigns Yet</h3>
              <p className="text-slate-500 mt-2">Create your first monthly collection campaign to get started</p>
              {(isAdmin || isSuperAdmin) && (
                <Button 
                  onClick={() => setShowCreateDialog(true)} 
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Campaign
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map(campaign => (
                <CampaignCard key={campaign.id} campaign={campaign} onSelect={handleCampaignSelect} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Collection Tab - Original Toloba Functionality */}
        <TabsContent value="collection" className="mt-6 space-y-6">
          {/* Stats Cards - Scrollable on mobile */}
          <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 md:grid-cols-5 sm:overflow-visible">
            <Card className="p-3 sm:p-4 min-w-[140px] sm:min-w-0 flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Total</p>
                  <p className="text-lg sm:text-xl font-bold">{familyCount}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-4 min-w-[120px] sm:min-w-0 flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Paid</p>
                  <p className="text-lg sm:text-xl font-bold text-emerald-600">{contributedCount}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-4 min-w-[120px] sm:min-w-0 flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Visited</p>
                  <p className="text-lg sm:text-xl font-bold text-amber-600">{visitedCount}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-4 min-w-[120px] sm:min-w-0 flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Pending</p>
                  <p className="text-lg sm:text-xl font-bold text-red-600">{pendingCount}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-4 min-w-[130px] sm:min-w-0 flex-shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                  <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Amount</p>
                  <p className="text-lg sm:text-xl font-bold text-blue-600">₹{total.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Filters - Mobile optimized */}
          <Card className="p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
              <div>
                <Label className="text-xs text-slate-500">Month</Label>
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Mohalla</Label>
                <Select value={selectedMohalla || "all"} onValueChange={(v) => setSelectedMohalla(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Mohallas</SelectItem>
                    {mohallas.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sector</Label>
                <Select value={selectedSector || "all"} onValueChange={(v) => setSelectedSector(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Sectors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sectors</SelectItem>
                    {sectors.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Name, ITS, Mobile..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedMohalla('');
                    setSelectedSector('');
                    setSearchInput('');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </Card>

          {/* Collection Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead className="hidden sm:table-cell">ITS</TableHead>
                    <TableHead>HOF Name</TableHead>
                    <TableHead className="hidden md:table-cell">Mobile</TableHead>
                    <TableHead className="hidden lg:table-cell">Mohalla</TableHead>
                    <TableHead className="hidden md:table-cell">Sector</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Amount</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : filteredContributions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No contributions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContributions.slice(0, 50).map((item, idx) => (
                      <TableRow key={item.hof_id}>
                        <TableCell className="text-slate-400">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm hidden sm:table-cell">{item.hof_its || '-'}</TableCell>
                        <TableCell className="font-medium">{item.hof_name}</TableCell>
                        <TableCell className="hidden md:table-cell">{item.mobile || '-'}</TableCell>
                        <TableCell className="hidden lg:table-cell">{item.mohalla}</TableCell>
                        <TableCell className="hidden md:table-cell">{item.sector}</TableCell>
                        <TableCell>
                        {item.status === 'paid' ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                        ) : item.status === 'visited' ? (
                          <Badge className="bg-amber-100 text-amber-700">Visited</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {item.amount > 0 ? `₹${item.amount.toLocaleString('en-IN')}` : '-'}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {item.status !== 'paid' && item.status !== 'visited' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => {
                                    setPaymentItem(item);
                                    setPaymentAmount('500');
                                  }}
                                  data-testid={`paid-btn-${item.hof_id}`}
                                >
                                  <Check className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Paid</span>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                  onClick={() => setNotPaidItem(item)}
                                  data-testid={`visited-btn-${item.hof_id}`}
                                >
                                  <Eye className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Visited</span>
                                </Button>
                              </>
                            )}
                            {item.status === 'visited' && (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => {
                                  setPaymentItem(item);
                                  setPaymentAmount('500');
                                }}
                                data-testid={`paid-btn-${item.hof_id}`}
                              >
                                <Check className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Mark Paid</span>
                              </Button>
                            )}
                            {item.status === 'paid' && !item.receipt_sent && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await api.post(`/toloba/${item.hof_id}/receipt?month=${selectedMonth}`);
                                    toast.success('Thank you message sent');
                                    fetchContributions();
                                  } catch {}
                                }}
                              >
                                <Send className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Send Thanks</span>
                              </Button>
                            )}
                            {item.receipt_sent && (
                              <Badge variant="outline" className="text-emerald-600">
                                <MessageSquare className="w-3 h-3 mr-1" /> Sent
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
            {filteredContributions.length > 50 && (
              <div className="p-4 text-center text-sm text-slate-500 border-t">
                Showing first 50 of {filteredContributions.length} families. Use filters to narrow results.
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Toloba Campaign</DialogTitle>
            <DialogDescription>Create a new monthly collection campaign</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="e.g., 16mi Raat Darees"
              />
            </div>
            
            <div>
              <Label>Month</Label>
              <Input
                type="month"
                value={newCampaign.month}
                onChange={(e) => setNewCampaign({ ...newCampaign, month: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="Add any notes about this campaign..."
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Select Sectors</Label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-navy hover:text-navy-light">
                  <Checkbox
                    checked={selectedSectorsForCreate.length === availableSectors.length && availableSectors.length > 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedSectorsForCreate([...availableSectors]);
                      } else {
                        setSelectedSectorsForCreate([]);
                      }
                    }}
                  />
                  <span className="font-medium">Select All</span>
                </label>
              </div>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {availableSectors.map(sector => (
                  <label key={sector} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <Checkbox
                      checked={selectedSectorsForCreate.includes(sector)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSectorsForCreate([...selectedSectorsForCreate, sector]);
                        } else {
                          setSelectedSectorsForCreate(selectedSectorsForCreate.filter(s => s !== sector));
                        }
                      }}
                    />
                    <span className="text-sm">{sector}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">{selectedSectorsForCreate.length} of {availableSectors.length} sectors selected</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createCampaign} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle>{selectedCampaign?.name}</DialogTitle>
                <DialogDescription>
                  {formatMonth(selectedCampaign?.month)} • {selectedCampaign?.status}
                </DialogDescription>
              </div>
              {(isAdmin || isSuperAdmin) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDeleteCampaign}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Campaign
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {campaignDetails && (
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="incharges">Incharges</TabsTrigger>
                <TabsTrigger value="khidmat">Khidmat Guzar</TabsTrigger>
                <TabsTrigger value="collection">
                  <IndianRupee className="w-4 h-4 mr-1" />
                  Collection
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="p-3 text-center">
                    <p className="text-sm text-slate-500">Incharges</p>
                    <p className="text-2xl font-bold">{campaignDetails.campaign_incharges?.length || 0}</p>
                  </Card>
                  <Card className="p-3 text-center">
                    <p className="text-sm text-slate-500">Mohalla Heads</p>
                    <p className="text-2xl font-bold">{campaignDetails.mohalla_heads?.length || 0}</p>
                  </Card>
                  <Card className="p-3 text-center">
                    <p className="text-sm text-slate-500">Khidmat Guzars</p>
                    <p className="text-2xl font-bold">{campaignDetails.khidmat_guzars?.length || 0}</p>
                  </Card>
                  <Card className="p-3 text-center">
                    <p className="text-sm text-slate-500">Sectors</p>
                    <p className="text-2xl font-bold">{campaignDetails.sectors?.length || 0}</p>
                  </Card>
                </div>
                
                {/* Sector Progress */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sector-wise Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sector</TableHead>
                          <TableHead>Khidmat Guzar</TableHead>
                          <TableHead className="text-center">Families</TableHead>
                          <TableHead className="text-center">Paid</TableHead>
                          <TableHead className="text-center">Visited</TableHead>
                          <TableHead className="text-center">Pending</TableHead>
                          <TableHead className="text-right">Collected</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaignDetails.sector_stats?.map(stat => (
                          <TableRow key={stat.sector}>
                            <TableCell className="font-medium">{stat.sector}</TableCell>
                            <TableCell>
                              {stat.assigned_kg ? (
                                <div>
                                  <p className="font-medium">{stat.assigned_kg.full_name}</p>
                                  <p className="text-xs text-slate-500">ITS: {stat.assigned_kg.its_id || 'N/A'}</p>
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-amber-600">Not Assigned</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">{stat.family_count}</TableCell>
                            <TableCell className="text-center text-emerald-600 font-medium">{stat.paid_count}</TableCell>
                            <TableCell className="text-center text-amber-600">{stat.visited_count || 0}</TableCell>
                            <TableCell className="text-center text-red-600">{stat.pending_count}</TableCell>
                            <TableCell className="text-right font-medium">₹{stat.total_collected?.toLocaleString('en-IN') || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="incharges" className="space-y-4 mt-4">
                {/* Campaign Incharges */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Campaign Incharges</CardTitle>
                    {canManageCampaigns && (
                      <Button size="sm" onClick={() => setShowAssignIncharge(true)}>
                        <UserPlus className="w-4 h-4 mr-2" /> Assign
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {campaignDetails.campaign_incharges?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>ITS</TableHead>
                            <TableHead>Mobile</TableHead>
                            <TableHead>Assigned By</TableHead>
                            {canManageCampaigns && <TableHead className="text-center">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaignDetails.campaign_incharges.map((incharge, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{incharge.full_name}</TableCell>
                              <TableCell className="font-mono">{incharge.its_id || '-'}</TableCell>
                              <TableCell>{incharge.mobile || '-'}</TableCell>
                              <TableCell className="text-slate-500">{incharge.assigned_by}</TableCell>
                              {canManageCampaigns && (
                                <TableCell className="text-center">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleRemoveIncharge(incharge.member_id, incharge.full_name)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center text-slate-500 py-4">No incharges assigned yet</p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Mohalla Heads */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Mohalla Heads</CardTitle>
                    {canManageCampaigns && (
                      <Button size="sm" onClick={() => setShowAssignMohallaHead(true)}>
                        <Building className="w-4 h-4 mr-2" /> Assign Mohalla Head
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {campaignDetails.mohalla_heads?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mohalla</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>ITS</TableHead>
                            <TableHead>Mobile</TableHead>
                            {canManageCampaigns && <TableHead className="text-center">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaignDetails.mohalla_heads.map((head, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{head.mohalla}</TableCell>
                              <TableCell>{head.full_name}</TableCell>
                              <TableCell className="font-mono">{head.its_id || '-'}</TableCell>
                              <TableCell>{head.mobile || '-'}</TableCell>
                              {canManageCampaigns && (
                                <TableCell className="text-center">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleRemoveMohallaHead(head.member_id, head.full_name)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center text-slate-500 py-4">No Mohalla heads assigned yet</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="khidmat" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Khidmat Guzar Assignments</CardTitle>
                    {canAssignKG && (
                      <Button size="sm" onClick={() => setShowAssignKG(true)}>
                        <UserPlus className="w-4 h-4 mr-2" /> Assign Khidmat Guzar
                      </Button>
                    )}
                    {isAssignedMohallaHead && !canManageCampaigns && (
                      <div className="ml-2 flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700">
                          You can assign KGs for {userMohallaInCampaign}
                        </Badge>
                        {allowedSectorsForKG.length > 0 && (
                          <span className="text-xs text-slate-500">
                            ({allowedSectorsForKG.join(', ')})
                          </span>
                        )}
                        {allowedSectorsForKG.length === 0 && (
                          <span className="text-xs text-amber-600">
                            (No sectors in campaign for your mohalla)
                          </span>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sector</TableHead>
                          <TableHead>Assigned KG</TableHead>
                          <TableHead>ITS</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          {canManageCampaigns && <TableHead className="text-center">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaignDetails.sectors?.map(sector => {
                          const kg = campaignDetails.khidmat_guzars?.find(k => k.sector === sector);
                          return (
                            <TableRow key={sector}>
                              <TableCell className="font-medium">{sector}</TableCell>
                              <TableCell>{kg?.full_name || '-'}</TableCell>
                              <TableCell className="font-mono">{kg?.its_id || '-'}</TableCell>
                              <TableCell>{kg?.mobile || '-'}</TableCell>
                              <TableCell className="text-center">
                                {kg ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">Assigned</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600">Not Assigned</Badge>
                                )}
                              </TableCell>
                              {canManageCampaigns && (
                                <TableCell className="text-center">
                                  {kg && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleRemoveKG(sector, kg.full_name)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Collection Tab - For KGs and Mohalla Heads to collect payments */}
              <TabsContent value="collection" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <IndianRupee className="w-5 h-5" />
                      Collection for {formatMonth(campaignDetails?.month)}
                    </CardTitle>
                    <CardDescription>
                      Mark families as Paid or Not Paid
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Search */}
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search by Name, ITS, Mobile..."
                          value={campaignCollectionSearch}
                          onChange={(e) => setCampaignCollectionSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    {/* Role-based info banner - only for Mohalla Head, not for KG */}
                    {isAssignedMohallaHead && userMohallaInCampaign && !isAdmin && !isSuperAdmin && !isAssignedCampaignIncharge && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">
                          <strong>Mohalla Head:</strong> You can collect for families in <strong>{userMohallaInCampaign}</strong>
                        </p>
                      </div>
                    )}
                    
                    {/* Load families button - only show for Admin/Incharge/MH, not for KG (auto-loads) */}
                    {!isAssignedKG && campaignFamilies.length === 0 && !campaignFamiliesLoading && (
                      <div className="text-center py-8">
                        <Button
                          onClick={() => fetchCampaignFamilies(campaignDetails?.month, getCollectionSectors())}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={getCollectionSectors().length === 0}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Load Families for Collection
                        </Button>
                        {getCollectionSectors().length === 0 && (
                          <p className="text-sm text-amber-600 mt-2">
                            You don't have any sectors assigned for collection in this campaign
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* No sectors message for KG */}
                    {isAssignedKG && userSectorsInCampaign.length === 0 && !campaignFamiliesLoading && (
                      <div className="text-center py-8">
                        <p className="text-sm text-amber-600">
                          You don't have any sectors assigned for collection in this campaign
                        </p>
                      </div>
                    )}
                    
                    {campaignFamiliesLoading && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                      </div>
                    )}
                    
                    {campaignFamilies.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10">#</TableHead>
                            <TableHead>ITS</TableHead>
                            <TableHead>HOF Name</TableHead>
                            <TableHead>Sector</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaignFamilies
                            .filter(item => {
                              if (!campaignCollectionSearch) return true;
                              const searchLower = campaignCollectionSearch.toLowerCase();
                              return (
                                item.hof_name?.toLowerCase().includes(searchLower) ||
                                item.hof_its?.includes(campaignCollectionSearch) ||
                                item.mobile?.includes(campaignCollectionSearch)
                              );
                            })
                            .slice(0, 50)
                            .map((item, idx) => (
                              <TableRow key={item.hof_id}>
                                <TableCell className="text-slate-400">{idx + 1}</TableCell>
                                <TableCell className="font-mono text-sm">{item.hof_its || '-'}</TableCell>
                                <TableCell className="font-medium">{item.hof_name}</TableCell>
                                <TableCell>{item.sector}</TableCell>
                                <TableCell>
                                  {item.status === 'paid' ? (
                                    <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                                  ) : item.status === 'not_paid' ? (
                                    <Badge variant="destructive">Not Paid</Badge>
                                  ) : (
                                    <Badge variant="secondary">Pending</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.amount > 0 ? `₹${item.amount.toLocaleString('en-IN')}` : '-'}
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                  {item.status !== 'paid' && (
                                    <>
                                      <Button
                                        size="sm"
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => {
                                          setPaymentItem(item);
                                          setPaymentAmount('500');
                                          setSelectedMonth(campaignDetails?.month);
                                        }}
                                      >
                                        <Check className="w-4 h-4 mr-1" /> Paid
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-600 border-red-300 hover:bg-red-50"
                                        onClick={() => {
                                          setNotPaidItem(item);
                                          setSelectedMonth(campaignDetails?.month);
                                        }}
                                      >
                                        <X className="w-4 h-4 mr-1" /> Not Paid
                                      </Button>
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Incharge Dialog */}
      <Dialog open={showAssignIncharge} onOpenChange={(open) => {
        setShowAssignIncharge(open);
        if (!open) {
          setSelectedMember(null);
          setMemberSearch('');
          setMemberResults([]);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Campaign Incharge</DialogTitle>
          </DialogHeader>
          {renderMemberSearch()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignIncharge(false)}>Cancel</Button>
            <Button onClick={handleAssignIncharge} disabled={!selectedMember || saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign Incharge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Mohalla Head Dialog */}
      <Dialog open={showAssignMohallaHead} onOpenChange={(open) => {
        setShowAssignMohallaHead(open);
        if (!open) {
          setSelectedMember(null);
          setMemberSearch('');
          setMemberResults([]);
          setAssignMohalla('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Mohalla Head</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Mohalla</Label>
              <Select value={assignMohalla} onValueChange={setAssignMohalla}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Mohalla" />
                </SelectTrigger>
                <SelectContent>
                  {mohallas.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {renderMemberSearch()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignMohallaHead(false)}>Cancel</Button>
            <Button onClick={handleAssignMohallaHead} disabled={!selectedMember || !assignMohalla || saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign Mohalla Head
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign KG Dialog */}
      <Dialog open={showAssignKG} onOpenChange={(open) => {
        setShowAssignKG(open);
        if (!open) {
          setSelectedMember(null);
          setMemberSearch('');
          setMemberResults([]);
          setAssignSector('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Khidmat Guzar</DialogTitle>
            {isAssignedMohallaHead && !canManageCampaigns && (
              <DialogDescription>
                As Mohalla Head for {userMohallaInCampaign}, you can assign KGs to: {allowedSectorsForKG.length > 0 ? allowedSectorsForKG.join(', ') : 'No sectors available'}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Sector</Label>
              <Select value={assignSector} onValueChange={setAssignSector}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Sector" />
                </SelectTrigger>
                <SelectContent>
                  {allowedSectorsForKG.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {allowedSectorsForKG.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No sectors from your mohalla are part of this campaign
                </p>
              )}
            </div>
            {renderMemberSearch()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignKG(false)}>Cancel</Button>
            <Button onClick={handleAssignKG} disabled={!selectedMember || !assignSector || saving || allowedSectorsForKG.length === 0}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign Khidmat Guzar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!paymentItem} onOpenChange={() => setPaymentItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="font-medium">{paymentItem?.hof_name}</p>
              <p className="text-sm text-slate-500">ITS: {paymentItem?.hof_its} | {paymentItem?.mohalla} - {paymentItem?.sector}</p>
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={paymentRemarks}
                onChange={(e) => setPaymentRemarks(e.target.value)}
                placeholder="Add any notes..."
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={sendThankYou}
                onCheckedChange={setSendThankYou}
              />
              <span className="text-sm">Send Thank You Message</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentItem(null)}>Cancel</Button>
            <Button onClick={handlePaid} disabled={saving || !paymentAmount} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visited (Not Paid) Dialog */}
      <Dialog open={!!notPaidItem} onOpenChange={() => setNotPaidItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Visited</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="font-medium">{notPaidItem?.hof_name}</p>
              <p className="text-sm text-slate-500">{notPaidItem?.mohalla} - {notPaidItem?.sector}</p>
            </div>
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea
                value={notPaidReason}
                onChange={(e) => setNotPaidReason(e.target.value)}
                placeholder="Enter any notes about the visit..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotPaidItem(null)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleNotPaid} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Mark Visited
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
